import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  IconButton,
  Paper,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  SupportAgent as SupportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { t } from '../utils/translations';
import { useAIChat } from '../hooks/useAIChat';
import { useAuth } from '../context/AuthContext';

const Support = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Get token from localStorage
  const token = localStorage.getItem('token');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const {
    isConnected,
    messages,
    currentResponse,
    isStreaming,
    error,
    sendMessage,
    reconnect,
    clearError,
  } = useAIChat(token);

  // Check if error is authentication-related
  const isAuthError = error && (
    error.toLowerCase().includes('authentication') ||
    error.toLowerCase().includes('token') ||
    error.toLowerCase().includes('unauthorized') ||
    error.toLowerCase().includes('login')
  );

  const handleLoginRedirect = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isAuthenticated) {
      scrollToBottom();
    }
  }, [messages, currentResponse, isAuthenticated]);

  // Don't render chat UI if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const handleSendMessage = () => {
    if (!messageText.trim() || isStreaming || !isConnected) return;
    sendMessage(messageText.trim());
    setMessageText('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleCloseSnackbar = () => {
    clearError();
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6, height: 'calc(100vh - 200px)' }}>
      <Paper
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
            borderRadius: 2, 
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
              bgcolor: 'primary.main', 
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
          >
            <SupportIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {t('Support')}
            </Typography>
            <Chip
              size="small"
              label={isConnected ? t('Online') : t('Offline')}
              sx={{
                bgcolor: isConnected ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                color: 'white',
                fontSize: '0.7rem',
                height: 20,
              }}
            />
          </Box>
          {!isConnected && (
            <IconButton
              onClick={reconnect}
              sx={{ color: 'white' }}
              title="Reconnect"
            >
              <RefreshIcon />
            </IconButton>
          )}
        </Box>

        {/* Messages Container */}
        <Box
          ref={chatContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            bgcolor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.length === 0 && !currentResponse ? (
            <Box
              sx={{
              display: 'flex',
                flexDirection: 'column',
              alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <SupportIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                {t('No messages yet')}
              </Typography>
              <Typography variant="body2">
                {t('Start a conversation with our support team')}
              </Typography>
            </Box>
          ) : (
            <>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  {message.role === 'assistant' && (
                    <Avatar
                      sx={{ 
                        bgcolor: 'primary.main',
                        width: 32,
                        height: 32,
                      }}
                    >
                      <SupportIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  )}
                  <Box
                      sx={{ 
                      maxWidth: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                        color: message.role === 'user' ? 'white' : 'text.primary',
                        borderTopLeftRadius: message.role === 'assistant' ? 4 : 16,
                        borderTopRightRadius: message.role === 'user' ? 4 : 16,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <Typography variant="body1">{message.content}</Typography>
                    </Paper>
                    <Typography
                      variant="caption"
                      sx={{ 
                        mt: 0.5,
                        px: 1,
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                      }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                  {message.role === 'user' && (
                    <Avatar
                      sx={{ 
                        bgcolor: 'secondary.main',
                        width: 32,
                        height: 32,
                      }}
                    >
                      U
                    </Avatar>
                  )}
              </Box>
              ))}

              {/* Streaming response */}
              {isStreaming && currentResponse && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SupportIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
            borderRadius: 2, 
                      bgcolor: '#e3f2fd',
                      borderTopLeftRadius: 4,
                      maxWidth: '70%',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <Typography variant="body1">
                      {currentResponse}
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: 8,
                          height: 16,
              bgcolor: 'primary.main', 
                          ml: 0.5,
                          animation: 'blink 1s infinite',
                          '@keyframes blink': {
                            '0%, 50%': { opacity: 1 },
                            '51%, 100%': { opacity: 0 },
                          },
                        }}
                      />
              </Typography>
                  </Paper>
            </Box>
              )}

              {/* Loading indicator when waiting for stream to start */}
              {isStreaming && !currentResponse && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SupportIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'white',
                      borderTopLeftRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                  </Paper>
                </Box>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
              </Box>
              
        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={t('Type your message...')}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isStreaming || !isConnected}
            sx={{
              '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                bgcolor: '#f5f5f5',
                '&:hover': {
                  bgcolor: '#eeeeee',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!messageText.trim() || isStreaming || !isConnected}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            {isStreaming ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
                </Box>
      </Paper>
      
      <Snackbar 
        open={!!error}
        autoHideDuration={isAuthError ? null : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          elevation={6} 
          variant="filled" 
          onClose={handleCloseSnackbar} 
          severity="error"
          sx={{ width: '100%' }}
          action={
            isAuthError ? (
              <Button color="inherit" size="small" onClick={handleLoginRedirect}>
                {t('Login')}
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Support; 
